import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  TrendingUp,
  Award,
  Target,
  Settings,
  ChevronRight,
  Code,
  Palette,
  Briefcase,
  MessageCircle,
  LogOut,
  Users,
  Shield,
  PieChart,
  FileText,
  User,
  UserCog,
  Menu,
  X,
  Trophy,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import "./AuthenticatedLayout.css";

const AuthenticatedLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const skillCategories = [
    { name: "Technical Skills", count: 15, icon: Code },
    { name: "Design & Creative", count: 8, icon: Palette },
    { name: "Business & Management", count: 12, icon: Briefcase },
    { name: "Soft Skills", count: 6, icon: MessageCircle },
  ];

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false); // Close sidebar on desktop
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  return (
    <div className="authenticated-layout">
      {/* Mobile Header */}
      {isMobile && (
        <header className="mobile-header">
          <button
            className="mobile-menu-btn"
            onClick={toggleSidebar}
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
          <Link to="/" className="mobile-logo">
            <img src="/logo.png" alt="ProSkillNest" className="logo-image" />
          </Link>
          <div className="mobile-user">
            <div className="user-avatar">
              {isAdmin() ? <UserCog size={18} /> : <User size={18} />}
            </div>
          </div>
        </header>
      )}

      {/* Sidebar Overlay for mobile */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <aside className={`auth-sidebar ${sidebarOpen ? "mobile-open" : ""}`}>
        {isMobile && (
          <div className="mobile-sidebar-header">
            <Link to="/" className="logo" onClick={closeSidebar}>
              <img src="/logo.png" alt="ProSkillNest" className="logo-image" />
            </Link>
            <button
              className="close-sidebar-btn"
              onClick={closeSidebar}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
        )}

        {!isMobile && (
          <div className="sidebar-header">
            <div className="logo">
              <Link to="/" className="logo">
                <img
                  src="/logo.png"
                  alt="ProSkillNest"
                  className="logo-image"
                />
              </Link>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          <div className="nav-section">
            <Link
              to="/dashboard"
              className={`nav-item ${isActive("/dashboard") ? "active" : ""}`}
              onClick={isMobile ? closeSidebar : undefined}
            >
              <BarChart3 size={18} />
              <span>Dashboard</span>
            </Link>

            {!isAdmin() && (
              <>
                <Link
                  to="/learning-paths"
                  className={`nav-item ${
                    isActive("/learning-paths") ? "active" : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <Target size={18} />
                  <span>Learning Paths</span>
                </Link>
                <Link
                  to="/assessments"
                  className={`nav-item ${
                    isActive("/assessments") || isActive("/assessment")
                      ? "active"
                      : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <Award size={18} />
                  <span>Assessments</span>
                </Link>
                <Link
                  to="/progress"
                  className={`nav-item ${
                    isActive("/progress") ? "active" : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <TrendingUp size={18} />
                  <span>Progress</span>
                </Link>
                <Link
                  to="/leaderboard"
                  className={`nav-item ${
                    isActive("/leaderboard") ? "active" : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <Trophy size={18} />
                  <span>Leaderboard</span>
                </Link>
              </>
            )}

            {isAdmin() && (
              <>
                <Link
                  to="/admin/users"
                  className={`nav-item ${
                    isActive("/admin/users") ? "active" : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <Users size={18} />
                  <span>User Management</span>
                </Link>
                <Link
                  to="/admin/assessments"
                  className={`nav-item ${
                    isActive("/admin/assessments") ? "active" : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <FileText size={18} />
                  <span>Assessment Management</span>
                </Link>
                <Link
                  to="/admin/analytics"
                  className={`nav-item ${
                    isActive("/admin/analytics") ? "active" : ""
                  }`}
                  onClick={isMobile ? closeSidebar : undefined}
                >
                  <PieChart size={18} />
                  <span>Analytics</span>
                </Link>
              </>
            )}
          </div>

          {!isAdmin() && (
            <div className="nav-section">
              <div className="nav-section-title">SKILL CATEGORIES</div>
              {skillCategories.map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <Link
                    key={index}
                    to={`/assessments?category=${encodeURIComponent(
                      category.name,
                    )}`}
                    className="nav-item"
                    onClick={isMobile ? closeSidebar : undefined}
                  >
                    <IconComponent size={18} className="category-icon" />
                    <span>{category.name}</span>
                    <ChevronRight size={14} className="nav-arrow" />
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              {isAdmin() ? <UserCog size={20} /> : <User size={20} />}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">
                {isAdmin() ? (
                  <>
                    <Shield size={12} />
                    Admin
                  </>
                ) : (
                  "User"
                )}
              </div>
            </div>
          </div>

          <Link
            to="/settings"
            className={`nav-item ${isActive("/settings") ? "active" : ""}`}
            onClick={isMobile ? closeSidebar : undefined}
          >
            <Settings size={18} />
            <span>Settings</span>
          </Link>
          <button onClick={handleLogout} className="nav-item logout-btn">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`auth-main-content ${isMobile ? "mobile" : ""}`}>
        {children}
      </main>
    </div>
  );
};

export default AuthenticatedLayout;
